import React, { useCallback, useMemo, useState } from 'react';
import { type AutosaveState, AutosaveStatus } from '@components/AutosaveStatus';
import type { Medication } from '@components/Patient/MedicationsDataBlock';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Box, Button, Chip, IconButton, useTheme } from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridDensity,
  type GridRowId,
  type GridRowModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  useGridApiRef,
} from '@mui/x-data-grid';
import { useEventTracking } from '@services/statsig';

interface PatientMedicationsEditCardProps {
  medications: Medication[];
  onMedicationsChange: (medications: Medication[]) => void;
  isLoading?: boolean;
  patientId?: string;
  /** Optional autosave status to display in toolbar */
  autosaveStatus?: AutosaveState;
  /** Whether there are unsaved changes (for autosave indicator) */
  isDirty?: boolean;
  /** Last saved timestamp (for autosave indicator) */
  lastSavedAt?: Date | null;
}

// Custom toolbar with column selector and add button
interface CustomToolbarProps {
  onAddRow: () => void;
  isLoading: boolean;
  buttonLabel: string;
  autosaveStatus?: AutosaveState;
  isDirty?: boolean;
  lastSavedAt?: Date | null;
}

const CustomToolbar: React.FC<CustomToolbarProps> = ({
  onAddRow,
  isLoading,
  buttonLabel,
  autosaveStatus,
  isDirty,
  lastSavedAt,
}) => {
  return (
    <GridToolbarContainer
      sx={{
        mb: 3,
        gap: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        '& .MuiButton-root': {
          borderRadius: '20px',
          textTransform: 'none',
          fontWeight: 500,
        },
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
      </Box>
      <Box display="flex" alignItems="center" gap={2}>
        {autosaveStatus !== undefined && (
          <AutosaveStatus
            status={autosaveStatus}
            isDirty={isDirty ?? false}
            lastSavedAt={lastSavedAt}
          />
        )}
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={onAddRow}
          disabled={isLoading}
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            bgcolor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
            '&:disabled': {
              bgcolor: 'action.disabledBackground',
              color: 'action.disabled',
            },
          }}
        >
          {buttonLabel}
        </Button>
      </Box>
    </GridToolbarContainer>
  );
};

const formatDate = (date: Date | string | null): string => {
  if (!date) return '';
  if (typeof date === 'string') return date.split('T')[0];
  return date.toISOString().split('T')[0];
};

// Fields that should always be visible regardless of data
const ALWAYS_VISIBLE_MEDICATION_FIELDS = ['medication_name', 'isActive', 'actions'];

export const PatientMedicationsEditCard: React.FC<PatientMedicationsEditCardProps> = ({
  medications,
  onMedicationsChange,
  isLoading = false,
  patientId,
  autosaveStatus,
  isDirty,
  lastSavedAt,
}) => {
  const theme = useTheme();
  const [density, setDensity] = useState<GridDensity>('compact');
  const apiRef = useGridApiRef();
  const { patient: patientTracking } = useEventTracking();

  // Calculate initial column visibility based on data (hide empty columns)
  const initialColumnVisibilityModel = useMemo(() => {
    const visibilityModel: GridColumnVisibilityModel = {};
    const fields = [
      'medication_name',
      'generic_name',
      'formulation',
      'strength',
      'route_of_administration',
      'dosage_amount',
      'frequency',
      'timing',
      'start_date',
      'end_date',
      'indication',
      'isActive',
      'actions',
    ];

    fields.forEach(field => {
      // Always show essential fields
      if (ALWAYS_VISIBLE_MEDICATION_FIELDS.includes(field)) {
        visibilityModel[field] = true;
        return;
      }

      // When there's no data, show all columns
      if (medications.length === 0) {
        visibilityModel[field] = true;
        return;
      }

      // Only show columns that have at least one non-empty value
      const hasValue = medications.some(med => {
        const value = med[field as keyof Medication];
        return value !== null && value !== undefined && value !== '';
      });
      visibilityModel[field] = hasValue;
    });

    return visibilityModel;
  }, [medications]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    initialColumnVisibilityModel,
  );

  const handleDeleteClick = useCallback(
    (id: GridRowId) => {
      const updatedMedications = medications.filter((_, index) => index !== id);
      onMedicationsChange(updatedMedications);
      if (patientId) {
        patientTracking.updated(patientId, {
          source: 'context_bar',
          fields_updated: ['medications_item_deleted'],
        });
      }
    },
    [medications, onMedicationsChange, patientId, patientTracking],
  );

  const processRowUpdate = useCallback(
    (newRow: GridRowModel) => {
      const rowIndex = newRow.id as number;
      const isNew = (medications[rowIndex] as any)?.isNew;
      const updatedRow = { ...newRow, isNew: false } as unknown as Medication;
      const updatedMedications = medications.map((med, index) =>
        index === rowIndex ? updatedRow : med,
      );
      onMedicationsChange(updatedMedications);
      if (patientId) {
        patientTracking.updated(patientId, {
          source: 'context_bar',
          fields_updated: [isNew ? 'medications_item_added' : 'medications_item_edited'],
        });
      }
      return updatedRow;
    },
    [medications, onMedicationsChange, patientId, patientTracking],
  );

  const handleAddRow = () => {
    const newMedication: Medication = {
      medication_name: `New Medication ${medications.length + 1}`,
      generic_name: null,
      formulation: null,
      strength: null,
      route_of_administration: null,
      dosage_amount: null,
      frequency: null,
      timing: null,
      start_date: null,
      end_date: null,
      indication: null,
      isActive: true,
    };
    const updatedMedications = [...medications, { ...newMedication, isNew: true }];
    onMedicationsChange(updatedMedications);

    // Focus the first cell of the new row after a short delay
    const newIndex = medications.length; // The index of the new row
    setTimeout(() => {
      const firstColumn = 'medication_name';
      if (apiRef.current) {
        apiRef.current.startCellEditMode({
          id: newIndex,
          field: firstColumn,
        });
      }
    }, 100);

    if (patientId) {
      patientTracking.updated(patientId, {
        source: 'context_bar',
        fields_updated: ['medications_add_button_clicked'],
      });
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'medication_name',
        headerName: 'Medication',
        minWidth: 150,
        flex: 1.5,
        editable: true,
      },
      {
        field: 'generic_name',
        headerName: 'Generic',
        minWidth: 120,
        flex: 1,
        editable: true,
      },
      {
        field: 'formulation',
        headerName: 'Form',
        minWidth: 80,
        flex: 0.7,
        editable: true,
      },
      {
        field: 'strength',
        headerName: 'Strength',
        minWidth: 80,
        flex: 0.7,
        editable: true,
      },
      {
        field: 'route_of_administration',
        headerName: 'Route',
        minWidth: 60,
        flex: 0.6,
        editable: true,
      },
      {
        field: 'dosage_amount',
        headerName: 'Dosage',
        minWidth: 80,
        flex: 0.7,
        editable: true,
      },
      {
        field: 'frequency',
        headerName: 'Frequency',
        minWidth: 100,
        flex: 0.8,
        editable: true,
      },
      {
        field: 'timing',
        headerName: 'Timing',
        minWidth: 80,
        flex: 0.7,
        editable: true,
      },
      {
        field: 'start_date',
        headerName: 'Start Date',
        flex: 1,
        editable: true,
        type: 'date',
        valueGetter: (_, row) => {
          const value = row.start_date;
          return value ? new Date(value) : null;
        },
        valueFormatter: (_, row) => formatDate(row.start_date),
      },
      {
        field: 'end_date',
        headerName: 'End Date',
        flex: 1,
        editable: true,
        type: 'date',
        valueGetter: (_, row) => {
          const value = row.end_date;
          return value ? new Date(value) : null;
        },
        valueFormatter: (_, row) => formatDate(row.end_date),
      },
      {
        field: 'indication',
        headerName: 'Indication',
        flex: 2,
        editable: true,
      },
      {
        field: 'isActive',
        headerName: 'Status',
        flex: 1,
        editable: true,
        type: 'boolean',
        renderCell: params => {
          return (
            <Chip
              label={params.value ? 'Active' : 'Inactive'}
              size="small"
              sx={{
                borderRadius: '999px',
                bgcolor: params.value ? 'rgba(103, 80, 164, 0.12)' : 'rgba(121, 116, 126, 0.12)',
                color: params.value ? 'rgba(103, 80, 164, 1)' : 'rgba(121, 116, 126, 1)',
                fontWeight: 500,
                border: 'none',
              }}
            />
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 50,
        sortable: false,
        filterable: false,
        editable: false,
        renderCell: params => (
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              handleDeleteClick(params.id);
            }}
            sx={{
              color: 'grey.500',
              '&:hover': {
                color: 'error.main',
              },
            }}
          >
            <RemoveCircleOutlineIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [handleDeleteClick],
  );

  const rows = useMemo(() => {
    return medications.map((med, index) => ({ ...med, id: index }));
  }, [medications]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGrid
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          density={density}
          onDensityChange={newDensity => setDensity(newDensity)}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          disableRowSelectionOnClick
          processRowUpdate={processRowUpdate}
          hideFooter
          slots={{
            toolbar: () => (
              <CustomToolbar
                onAddRow={handleAddRow}
                isLoading={isLoading}
                buttonLabel="Add Medication"
                autosaveStatus={autosaveStatus}
                isDirty={isDirty}
                lastSavedAt={lastSavedAt}
              />
            ),
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: 'action.hover',
              borderRadius: '12px',
              fontWeight: 600,
              borderBottom: 'none',
            },
            '& .MuiDataGrid-row': {
              borderRadius: '8px',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            },
            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
            },
          }}
        />
      </Box>
    </Box>
  );
};
